import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { makeRikudo, pixel, rikudoConflicts, checkRikudoWin } from "@/lib/rikudo";

const HEX = "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)";

export function RikudoGame({ R, givenRatio, onWin, resetKey }) {
  const { t } = useTranslation();
  const [puzzle, setPuzzle] = useState(null);
  const [placed, setPlaced] = useState({});
  const [selected, setSelected] = useState(null);

  const start = useCallback(() => {
    const p = makeRikudo(R, givenRatio);
    setPuzzle(p);
    const init = {};
    p.cells.forEach((c) => { if (c.given) init[c.key] = c.number; });
    setPlaced(init);
    setSelected(null);
  }, [R, givenRatio]);

  useEffect(() => { start(); }, [start, resetKey]);

  const conflicts = useMemo(() => (puzzle ? rikudoConflicts(placed, puzzle) : new Set()), [placed, puzzle]);
  const solved = useMemo(() => (puzzle ? checkRikudoWin(placed, puzzle) : false), [placed, puzzle]);
  useEffect(() => { if (solved) onWin?.(); }, [solved, onWin]);

  const usedNumbers = useMemo(() => new Set(Object.values(placed)), [placed]);

  const size = R <= 1 ? 40 : R === 2 ? 34 : 26;
  const layout = useMemo(() => {
    if (!puzzle) return null;
    const pts = puzzle.cells.map((c) => ({ c, p: pixel(c, size) }));
    const xs = pts.map((o) => o.p.x), ys = pts.map((o) => o.p.y);
    const minX = Math.min(...xs), minY = Math.min(...ys);
    const w = size * Math.sqrt(3), h = size * 2;
    return {
      pts,
      offX: -minX + w / 2,
      offY: -minY + h / 2,
      width: Math.max(...xs) - minX + w,
      height: Math.max(...ys) - minY + h,
      w, h,
    };
  }, [puzzle, size]);

  const tapCell = (cell) => {
    if (cell.given || solved) return;
    setPlaced((prev) => {
      const next = { ...prev };
      if (selected == null) {
        delete next[cell.key];
        return next;
      }
      for (const k of Object.keys(next)) {
        if (next[k] === selected && !puzzle.cells.find((x) => x.key === k)?.given) delete next[k];
      }
      next[cell.key] = selected;
      return next;
    });
  };

  if (!puzzle || !layout) return null;

  return (
    <div className="flex flex-col items-center" data-testid="rikudo-game">
      <p className="text-xs text-foreground/45 text-center max-w-md mb-6 leading-relaxed">{t("games.rikudoRules")}</p>

      <div
        className="relative mx-auto"
        style={{ width: layout.width, height: layout.height, maxWidth: "92vw" }}
        data-testid="rikudo-board"
      >
        {layout.pts.map(({ c, p }) => {
          const val = placed[c.key];
          const bad = conflicts.has(c.key);
          return (
            <button
              key={c.key}
              data-testid={`rikudo-cell-${c.key}`}
              onClick={() => tapCell(c)}
              disabled={c.given || solved}
              style={{
                position: "absolute",
                left: layout.offX + p.x - layout.w / 2,
                top: layout.offY + p.y - layout.h / 2,
                width: layout.w,
                height: layout.h,
                clipPath: HEX,
              }}
              className={`flex items-center justify-center font-display transition-colors duration-150
                ${bad ? "bg-red-500/35 text-red-100" : c.given ? "bg-primary/30 text-primary" : val ? "bg-white/[0.1] text-foreground/90" : "bg-white/[0.04] hover:bg-primary/15"}`}
            >
              <span style={{ fontSize: size * 0.6 }}>{val || ""}</span>
            </button>
          );
        })}
      </div>

      <p className="text-[11px] uppercase tracking-[0.18em] text-foreground/40 mt-8 mb-3">{t("games.selectNumber")}</p>
      <div className="flex flex-wrap justify-center gap-1.5 max-w-lg" data-testid="rikudo-numpad">
        {Array.from({ length: puzzle.K }, (_, i) => i + 1).map((num) => {
          const used = usedNumbers.has(num);
          const active = selected === num;
          return (
            <button
              key={num}
              data-testid={`rikudo-num-${num}`}
              onClick={() => setSelected(active ? null : num)}
              className={`w-9 h-9 rounded-lg text-sm font-display transition-colors border
                ${active ? "border-primary bg-primary/25 text-primary" : used ? "border-white/10 text-foreground/25" : "border-white/15 text-foreground/70 hover:border-primary/50"}`}
            >
              {num}
            </button>
          );
        })}
      </div>

      <button
        onClick={start}
        data-testid="rikudo-new-btn"
        className="mt-7 inline-flex items-center gap-2 text-xs uppercase tracking-wider text-foreground/45 hover:text-primary transition-colors"
      >
        <RefreshCw size={13} /> {t("games.newGrid")}
      </button>
    </div>
  );
}
